import {
  APIComponentAttributes,
  APIOrganizationUser,
  APIScopeData,
  ArdoqId,
} from '@ardoq/api-types';
import { ArdoqError, Maybe } from '@ardoq/common-helpers';
import { EnhancedScopeData } from '@ardoq/data-model';
import { parseLocaleDateString } from '@ardoq/date-time';
import { s16 } from '@ardoq/design-tokens';
import {
  DataTypeDefinition,
  RowClassParams,
  themeQuartz,
  GetRowIdParams,
  CellSelectionOptions,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { columnOperations } from '../columns/columnOperations';
import { defaultColumnDefinition } from '../columns/defaultColumnDefinition';
import { getColumnMainMenuItems } from '../columns/mainColumnMenuItems';
import { mapReportColumnDefinitionToAgGridFormatIgnoringHierarchy } from '../columns/mapReportColumnDefinitionToAgGridFormat';
import {
  ExtendedRowNode,
  InventoryAccessControl,
  InventoryColumnDefinitionWithSelectionState,
  InventoryCommands,
  InventoryConfig,
  InventoryContext,
  InventoryDatasourceSelection,
  MetamodelWithTriplesWithOrigin,
} from '../types';
import { type I18nLocale } from '@ardoq/i18n';
import { registerModules } from '../moduleRegistry';
import { AgEventHandlers } from '../AgEventHandlers';
import { getDeleteColumnDefinition } from './getDeleteColumnDefinition';

registerModules();

/**
 * AG Grid requires a unique ID for each row.
 * It's possible for a row to be pinned and in the main table (when you create new row and then change filters or sorting).
 * We make sure that the ID is unique in such case.
 */
const getRowId = (params: GetRowIdParams<APIComponentAttributes>) => {
  return params.data._id;
};

const getRowClass = (params: RowClassParams<APIComponentAttributes>) =>
  (params.node as ExtendedRowNode)?._hasError ? 'row-with-error' : undefined;

const cellSelection: CellSelectionOptions = {
  suppressMultiRanges: true,
  enableHeaderHighlight: true,
  handle: {
    mode: 'fill',
    suppressClearOnFillReduction: true,
    direction: 'y',
  },
};

export type CreateComponentsInventoryProps = {
  initialColumnDefinitions: InventoryColumnDefinitionWithSelectionState[];
  error: Maybe<ArdoqError>;
  datasourceSelection: InventoryDatasourceSelection;
  initialScopeData: EnhancedScopeData;
  apiScopeData: APIScopeData;
  users: APIOrganizationUser[];
  metaModel: MetamodelWithTriplesWithOrigin;
  components: Partial<APIComponentAttributes>[];
  accessControl: InventoryAccessControl;
  config: InventoryConfig;
  lastRecalculationByFieldsId: Record<ArdoqId, string>;
  i18nLocale: I18nLocale;
  richTextEditorConfig: InventoryContext['richTextEditorConfig'];
  /**
   * Translations for AG Grid built-in strings. See `Inventory` for details.
   */
  agGridLocaleText?: Record<string, string>;
};

export const CreateComponentsInventory = ({
  initialColumnDefinitions,
  commands,
  datasourceSelection,
  users,
  initialScopeData,
  apiScopeData,
  metaModel,
  components,
  accessControl,
  config,
  lastRecalculationByFieldsId,
  i18nLocale,
  richTextEditorConfig,
  agGridLocaleText,
}: CreateComponentsInventoryProps & { commands: InventoryCommands }) => {
  /*
   * We want popups (date picker and dropdowns to stay inside the grid container)
   * when scrolling horizontally. Therefore, we cannot use the default document.body.
   */
  const gridRef = useRef<HTMLDivElement>(null);
  const gridApiRef = useRef<GridApi<APIComponentAttributes> | null>(null);

  const onGridReady = (params: GridReadyEvent<APIComponentAttributes>) => {
    gridApiRef.current = params.api;
  };

  const columnDefinitionsInAgGridFormat = useMemo(() => {
    const mappedColumns = initialColumnDefinitions
      .filter(
        /**
         * You cannot create file or reference fields when creating components - they don't have ids yet.
         */
        reportColumn =>
          !columnOperations.isFileColumn(reportColumn) &&
          !columnOperations.isReferenceColumn(reportColumn)
      )
      .map(column =>
        mapReportColumnDefinitionToAgGridFormatIgnoringHierarchy(
          column,
          datasourceSelection
        )
      );

    const nameColumn = mappedColumns.find(col => col.field === 'name');
    const nameColumnPinned = nameColumn?.pinned ?? null;

    const actionColumn = getDeleteColumnDefinition(nameColumnPinned);

    return [actionColumn, ...mappedColumns];
  }, [initialColumnDefinitions, datasourceSelection]);

  const initialContext: InventoryContext = {
    commands,
    users,
    scopeData: initialScopeData,
    apiScopeData,
    metaModel,
    config,
    datasourceSelection,
    accessControl,
    hierarchy: { isHierarchyEnabled: false },
    lastRecalculationByFieldsId,
    externalDocumentsByComponentId: {},
    i18nLocale,
    isLockingEnabled: false,
    tagsByWorkspaceId: {},
    richTextEditorConfig,
    externalSubquery: null,
  };

  // Seed context.i18nLocale on mount so AG Grid framework-locked callbacks
  // (cell renderers / value getters / formatters) read a locale. The
  // create-components flow is a transient surface; we intentionally do not
  // listen for subsequent i18nLocale changes here so that any upstream
  // ref-stability regression of i18nLocale$ cannot force a full grid
  // header+cells refresh on every emission.
  useEffect(
    () => {
      const api = gridApiRef.current;
      const inventoryContext = api?.getGridOption(
        'context'
      ) as InventoryContext;
      if (!api || !inventoryContext) return;

      inventoryContext.i18nLocale = i18nLocale;
      api.refreshHeader();
      api.refreshCells({ force: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: i18nLocale excluded; see comment above.
    []
  );

  const customDataTypeDefinitions: {
    dateString: DataTypeDefinition<APIComponentAttributes>;
  } = useMemo(
    () => ({
      dateString: {
        baseDataType: 'dateString',
        extendsDataType: 'dateString',
        dateParser: (value: string | undefined) => {
          if (!value) return undefined;
          return parseLocaleDateString(value, config.locale) ?? undefined;
        },
        dateFormatter: (value: Date | undefined) => value?.toISOString(),
      },
    }),
    [config.locale]
  );

  return (
    <div
      ref={gridRef}
      style={{
        position: 'relative', // to make the popupParent work correctly
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        height: '100%',
        width: '100%',
        gap: s16,
      }}
    >
      <AgGridReact<APIComponentAttributes>
        localeText={agGridLocaleText}
        context={initialContext}
        rowData={components as APIComponentAttributes[]}
        theme={themeQuartz}
        debug={import.meta.env.DEBUG_INVENTORY}
        getRowId={getRowId}
        columnDefs={columnDefinitionsInAgGridFormat}
        rowModelType="clientSide"
        defaultColDef={defaultColumnDefinition}
        onCellKeyDown={AgEventHandlers.onCellKeyDown}
        getMainMenuItems={getColumnMainMenuItems}
        dataTypeDefinitions={customDataTypeDefinitions}
        getRowClass={getRowClass}
        popupParent={gridRef.current}
        cellSelection={cellSelection}
        processDataFromClipboard={AgEventHandlers.processDataFromClipboard}
        processCellForClipboard={AgEventHandlers.processCellForClipboard}
        onFirstDataRendered={params => params.api.autoSizeAllColumns()}
        suppressCsvExport={true}
        tooltipShowDelay={0}
        onGridReady={onGridReady}
        onColumnPinned={
          AgEventHandlers.syncPinningNameWithDeleteComponentColumn
        }
      />
    </div>
  );
};
